import { CheckCircle2, Database, XCircle } from 'lucide-react';
import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { Header } from './components/Header';
import { Link } from './components/Link';
import { creatorsBySlug } from './lib/catalog';
import { downloadCountsSnapshot, serverDownloadCountsSnapshot, subscribeToDownloadCounts } from './lib/downloadCounts';
import { readFollowedCreators, saveFollowedCreators } from './lib/followedCreators';
import { CreatorPage } from './pages/CreatorPage';
import { HomePage } from './pages/HomePage';
import { PackPage } from './pages/PackPage';
import { PacksPage } from './pages/PacksPage';
import { ProfilePage } from './pages/ProfilePage';
import { SearchPage } from './pages/SearchPage';

interface RouteState {
  pathname: string;
  search: string;
  href: string;
}

interface ToastState {
  message: string;
  tone: 'success' | 'error';
  key: number;
}

function currentRoute(): RouteState {
  return { pathname: window.location.pathname, search: window.location.search, href: `${window.location.pathname}${window.location.search}` };
}

function useRoute(): RouteState {
  const [route, setRoute] = useState(currentRoute);
  useEffect(() => {
    const update = () => setRoute(currentRoute());
    window.addEventListener('popstate', update);
    return () => window.removeEventListener('popstate', update);
  }, []);
  return route;
}

function safeRouteId(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

export default function App() {
  const route = useRoute();
  const previousRoute = useRef(route.href);
  const [toast, setToast] = useState<ToastState | null>(null);
  const [followedCreators, setFollowedCreators] = useState(readFollowedCreators);
  const isSearchRoute = route.pathname === '/search' || route.pathname === '/search/';
  useSyncExternalStore(subscribeToDownloadCounts, downloadCountsSnapshot, serverDownloadCountsSnapshot);

  useEffect(() => saveFollowedCreators(followedCreators), [followedCreators]);

  function toggleFollow(slug: string): void {
    setFollowedCreators((current) => current.includes(slug) ? current.filter((value) => value !== slug) : [...current, slug]);
  }

  function notify(message: string, tone: 'success' | 'error' = 'success'): void {
    setToast({ message, tone, key: Date.now() });
  }

  useEffect(() => {
    if (!toast) return;
    const timeout = window.setTimeout(() => setToast((current) => (current?.key === toast.key ? null : current)), 3400);
    return () => window.clearTimeout(timeout);
  }, [toast]);

  useEffect(() => {
    const creatorRoute = route.pathname.match(/^\/creators\/([^/]+)\/?$/);
    const creatorName = creatorRoute ? creatorsBySlug.get(safeRouteId(creatorRoute[1]))?.name : undefined;
    const section = route.pathname.startsWith('/profiles/')
      ? 'Profile Details'
      : route.pathname.startsWith('/creators/')
        ? creatorName ?? 'Creator'
        : route.pathname.startsWith('/packs/')
          ? 'Profile pack'
          : route.pathname === '/packs'
            ? 'Profile packs'
            : route.pathname === '/search'
              ? 'Browse profiles'
              : '';
    document.title = section ? `${section} · Retro Profiles` : 'Retro Profiles';
  }, [route.pathname]);

  useEffect(() => {
    if (previousRoute.current !== route.href) {
      document.getElementById('main-content')?.focus({ preventScroll: true });
      previousRoute.current = route.href;
    }
  }, [route.href]);

  let page;
  const profileMatch = route.pathname.match(/^\/profiles\/([^/]+)(?:\/[^/]+)?\/?$/);
  const packMatch = route.pathname.match(/^\/packs\/([^/]+)(?:\/[^/]+)?\/?$/);
  const creatorMatch = route.pathname.match(/^\/creators\/([^/]+)\/?$/);
  if (profileMatch) page = <ProfilePage key={profileMatch[1]} id={safeRouteId(profileMatch[1])} search={route.search} notify={notify} />;
  else if (creatorMatch) {
    const slug = safeRouteId(creatorMatch[1]);
    page = <CreatorPage key={slug} slug={slug} notify={notify} isFollowing={followedCreators.includes(slug)} onToggleFollow={() => toggleFollow(slug)} />;
  }
  else if (packMatch) page = <PackPage key={packMatch[1]} id={safeRouteId(packMatch[1])} notify={notify} />;
  else if (route.pathname === '/search' || route.pathname === '/search/') page = <SearchPage search={route.search} notify={notify} />;
  else if (route.pathname === '/packs' || route.pathname === '/packs/') page = <PacksPage notify={notify} />;
  else if (route.pathname === '/') page = <HomePage notify={notify} followedCreators={followedCreators} />;
  else page = <div className="empty-state"><Database size={36} /><h1>Page not found</h1><p>That address does not point to a page in the profile archive.</p><Link className="button secondary" href="/">Return home</Link></div>;

  return (
    <div className="app-shell">
      <a className="skip-link" href="#main-content">Skip to main content</a>
      <Header />
      <main className={isSearchRoute ? 'site-main search-site-main' : 'site-main'} id="main-content" tabIndex={-1}>{page}</main>
      <footer className="site-footer">
        <div><Link className="footer-brand" href="/"><Database size={17} /><strong>Retro Profiles</strong></Link><p>A community hub for sharing video scaler profiles. Always back up your scaler configuration before loading third-party files.</p></div>
        <nav aria-label="Footer navigation"><Link href="/search">Browse</Link><Link href="/packs">Profile packs</Link></nav>
      </footer>
      {toast && <div className={`toast ${toast.tone}`} role={toast.tone === 'error' ? 'alert' : 'status'} key={toast.key}>{toast.tone === 'success' ? <CheckCircle2 size={18} /> : <XCircle size={18} />}{toast.message}</div>}
    </div>
  );
}
